"use client";

import { useRef, useEffect, useState, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Data types
// ─────────────────────────────────────────────────────────────────────────────

export interface SundialItem {
  id: string;
  label: string;
  shortLabel?: string;     // ≤ 8 chars for canvas
  innerDesc: string;       // description in inner/middle ring
  outerDesc: string;       // outer ring detail
}

export interface SundialQuadrant {
  id: string;
  label: string;           // SPACE, PLACE, PACTS, PRACTICES
  subtitle: string;
  startRad: number;        // canvas angle: 0=right, clockwise
  endRad: number;
  colors: string[];        // 6 shades light→dark
  textDark: string;
  textLight: string;
  items: SundialItem[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Imagination Sundial data  (© 2020 Rob Shorter, Co-Created with Rob Hopkins, CC BY-SA 4.0)
// ─────────────────────────────────────────────────────────────────────────────

const R = Math.PI / 180;

export const SUNDIAL_QUADRANTS: SundialQuadrant[] = [
  {
    id: "space",
    label: "SPACE",
    subtitle: "Mental and emotional space that expands our capacity to imagine",
    startRad: Math.PI,           // 9 o'clock
    endRad: 3 * Math.PI / 2,    // 12 o'clock
    colors: ["#fce4ec", "#f8bbd0", "#f48fb1", "#e91e63", "#c2185b", "#880e4f"],
    textDark: "#7d1150",
    textLight: "#fff",
    items: [
      {
        id: "connecting",
        label: "Connecting",
        shortLabel: "Connect",
        innerDesc: "Connecting to others and the natural world",
        outerDesc: "Connecting to, and nourishing our relationship with, people and the natural world across generations, places and futures",
      },
      {
        id: "opening",
        label: "Opening",
        shortLabel: "Opening",
        innerDesc: "Opening collective imagination",
        outerDesc: "Making ways for collective imagining by connecting to the natural world, encouraging dreaming across generations, places and futures",
      },
      {
        id: "slowing",
        label: "Slowing",
        shortLabel: "Slowing",
        innerDesc: "Slowing down to sense differently",
        outerDesc: "Slowing down and opening up to other ways of being, knowing, being with others, and making sense of the world",
      },
      {
        id: "permission",
        label: "Permission",
        shortLabel: "Permiss.",
        innerDesc: "Permission to be creative",
        outerDesc: "Suspending our inner voice of judgment and giving ourselves permission to go to the edge of our comfort zone and beyond",
      },
      {
        id: "safe",
        label: "Safe",
        shortLabel: "Safe",
        innerDesc: "Feeling safe to imagine",
        outerDesc: "Feeling safe, both physically and emotionally, acknowledging and honouring our fear and vulnerabilities while daring to dream",
      },
      {
        id: "welcome",
        label: "Welcome",
        shortLabel: "Welcome",
        innerDesc: "Welcoming everyone's voice",
        outerDesc: "Feeling welcome to be ourselves and invited to participate with warmth and sincerity, regardless of who we are",
      },
    ],
  },
  {
    id: "place",
    label: "PLACE",
    subtitle: "Gathering places that provide platforms for collective imagining",
    startRad: 3 * Math.PI / 2,  // 12 o'clock
    endRad: 2 * Math.PI,         // 3 o'clock
    colors: ["#fff8e1", "#ffe082", "#ffd740", "#ffc107", "#ff8f00", "#e65100"],
    textDark: "#bf360c",
    textLight: "#fff",
    items: [
      {
        id: "street",
        label: "Street",
        shortLabel: "Street",
        innerDesc: "Car-free streets & public space",
        outerDesc: "Gathering with neighbours in our streets, gardens and car-free spaces to imagine together how our communities could be",
      },
      {
        id: "local",
        label: "Local",
        shortLabel: "Local",
        innerDesc: "Local community venues",
        outerDesc: "Local places that belong to and are run by communities — pubs, village halls, community gardens and neighbourhood hubs",
      },
      {
        id: "civic",
        label: "Civic",
        shortLabel: "Civic",
        innerDesc: "Civic & institutional spaces",
        outerDesc: "Community-run organisations and civic institutions that provide neutral platforms for imagination and co-creation",
      },
      {
        id: "natural",
        label: "Natural",
        shortLabel: "Natural",
        innerDesc: "Natural and wild places",
        outerDesc: "Restorative, close-to-nature spaces that nourish the imagination — parks, forests, rivers and wild corners of the city",
      },
      {
        id: "work",
        label: "Work",
        shortLabel: "Work",
        innerDesc: "Workplaces as imagination labs",
        outerDesc: "Workplaces reimagined as spaces for creativity, where teams gather to dream beyond the immediate demands of the day",
      },
      {
        id: "virtual",
        label: "Virtual",
        shortLabel: "Virtual",
        innerDesc: "Digital gathering places",
        outerDesc: "Online platforms and hybrid spaces that connect imaginations across distances, enabling collective dreaming at scale",
      },
    ],
  },
  {
    id: "pacts",
    label: "PACTS",
    subtitle: "Pacts of collaboration that catalyse imagination into action",
    startRad: 0,                 // 3 o'clock
    endRad: Math.PI / 2,         // 6 o'clock
    colors: ["#e8f5e9", "#a5d6a7", "#81c784", "#4caf50", "#43a047", "#1b5e20"],
    textDark: "#1b5e20",
    textLight: "#fff",
    items: [
      {
        id: "residents",
        label: "Residents",
        shortLabel: "Resdnts",
        innerDesc: "Residents as co-creators",
        outerDesc: "A community of residents who share a commitment to imagining and building the future of their neighbourhood together",
      },
      {
        id: "civil_society",
        label: "Civil Society",
        shortLabel: "Civil Soc",
        innerDesc: "Civil society organisations",
        outerDesc: "A broad coalition of civil society organisations and community groups that bring diverse perspectives to collective action",
      },
      {
        id: "business",
        label: "Business",
        shortLabel: "Business",
        innerDesc: "Businesses as partners",
        outerDesc: "Businesses that see their role as contributing to thriving communities and bring resources, skills and creativity to shared challenges",
      },
      {
        id: "authorities",
        label: "Authorities",
        shortLabel: "Authorit.",
        innerDesc: "Public authorities enabling change",
        outerDesc: "Public authorities of appropriate scale and jurisdictional boundary that create conditions for imagination to flourish",
      },
      {
        id: "institutions",
        label: "Institutions",
        shortLabel: "Institut.",
        innerDesc: "Knowledge institutions",
        outerDesc: "Universities, museums and other knowledge institutions with research capability that catalyse imagination with evidence",
      },
      {
        id: "catalysts",
        label: "Catalysts",
        shortLabel: "Catalysts",
        innerDesc: "Catalysts of possibility",
        outerDesc: "Inspiring individuals and organisations that convene groups and catalyse action through trust-building, storytelling and facilitation",
      },
    ],
  },
  {
    id: "practices",
    label: "PRACTICES",
    subtitle: "Practices that connect us and change our frame of possibility",
    startRad: Math.PI / 2,       // 6 o'clock
    endRad: Math.PI,             // 9 o'clock
    colors: ["#e3f2fd", "#90caf9", "#64b5f6", "#2196f3", "#1565c0", "#0d47a1"],
    textDark: "#0d47a1",
    textLight: "#fff",
    items: [
      {
        id: "yes_and",
        label: "Yes and what if",
        shortLabel: "Yes&If",
        innerDesc: "Yes, and… what if?",
        outerDesc: "Using the words 'yes, and' and 'what if' to open our conversations to possibility and overcome the inner critic that shuts down imagination",
      },
      {
        id: "celebrating",
        label: "Celebrating",
        shortLabel: "Celebratg",
        innerDesc: "Celebrating imagination",
        outerDesc: "Creating a sense of collective experience through ceremony and celebration that honours imagination and builds a culture of possibility",
      },
      {
        id: "playing",
        label: "Playing",
        shortLabel: "Playing",
        innerDesc: "Playing as adults",
        outerDesc: "Exploring the space between the known and the unknown through analogy, play and improvisation that unlocks new thinking",
      },
      {
        id: "making",
        label: "Making",
        shortLabel: "Making",
        innerDesc: "Making with our hands",
        outerDesc: "Using the hands to make things — from fabrication to craft, visual art and analog improvisation — to think through doing",
      },
      {
        id: "limits",
        label: "Limits",
        shortLabel: "Limits",
        innerDesc: "Limits that free imagination",
        outerDesc: "Setting clear and agreed boundaries and rules to free the imagination and enable play — constraints as creative catalysts",
      },
      {
        id: "inspiration",
        label: "Inspiration",
        shortLabel: "Inspire",
        innerDesc: "Sources of inspiration",
        outerDesc: "Sources of inspiration that allow people to see and imagine things differently — nature, art, travel, other cultures and futures",
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// AI results type
// ─────────────────────────────────────────────────────────────────────────────

export type SundialAIResults = Record<string, string[]>; // segmentId → [AI text 1, 2, 3]

// ─────────────────────────────────────────────────────────────────────────────
// Canvas geometry constants
// ─────────────────────────────────────────────────────────────────────────────

const R1 = 46;   // center circle outer radius
const R2 = 112;  // inner segment ring outer radius
const R3 = 178;  // outer description ring outer radius
const R4 = 208;  // label band outer radius
const R5 = 244;  // decorative spike outer radius
const SEG_GAP = 0.012; // radians gap between segments

function polarToCart(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

function annularSectorPath(
  cx: number, cy: number,
  r1: number, r2: number,
  startA: number, endA: number
): Path2D {
  const path = new Path2D();
  const s1 = polarToCart(cx, cy, r1, startA);
  const e1 = polarToCart(cx, cy, r1, endA);
  const s2 = polarToCart(cx, cy, r2, startA);
  const e2 = polarToCart(cx, cy, r2, endA);
  const large = endA - startA > Math.PI ? 1 : 0;
  path.moveTo(s2.x, s2.y);
  path.arc(cx, cy, r2, startA, endA, false);
  path.lineTo(e1.x, e1.y);
  path.arc(cx, cy, r1, endA, startA, true);
  path.lineTo(s2.x, s2.y);
  path.closePath();
  void (s1); void (e2); // suppress unused
  return path;
}

/** Normalise any angle to [0, 2π) */
function norm2pi(a: number): number {
  return ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

/**
 * Curved arc text — each character is individually positioned and rotated
 * along the arc so text bends with the circle.
 * Flipped in the bottom half (midA ∈ (0,π)) so text is never upside-down.
 */
function drawArcText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number, cy: number,
  r: number,
  startA: number, endA: number,
  fontSize: number,
  color: string,
  maxChars = 100
) {
  const midA = (startA + endA) / 2;
  const n = norm2pi(midA);
  // Bottom half = midA ∈ (0, π) in canvas coords → flip so text is never upside-down
  const flip = n > 0 && n < Math.PI;

  const display = text.length > maxChars ? text.slice(0, maxChars - 1) + "…" : text;

  ctx.save();
  ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";

  // In the bottom half reverse character order so they read left-to-right
  const chars = flip ? display.split("").reverse() : display.split("");
  const widths = chars.map(c => ctx.measureText(c).width);
  const totalWidth = widths.reduce((a, b) => a + b, 0);

  let cumWidth = 0;
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    const cw = widths[i];
    // Angular offset of this character's centre from midA
    const offset = cumWidth + cw / 2 - totalWidth / 2;
    const angle = flip
      ? midA - offset / r
      : midA + offset / r;

    const { x, y } = polarToCart(cx, cy, r, angle);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle + Math.PI / 2 + (flip ? Math.PI : 0));
    ctx.fillText(ch, 0, 0);
    ctx.restore();

    cumWidth += cw;
  }

  ctx.restore();
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface ImaginaryWheelProps {
  projectTitle?: string;
  projectDesc?: string;
  aiResults?: SundialAIResults;
  onAIFill?: (results: SundialAIResults) => void;
  onAIFillComplete?: (results: SundialAIResults) => void;
  onAISources?: (sources: { doc_title: string; doc_id: string; reason?: string }[]) => void;
}

export default function ImaginaryWheel({
  projectTitle = "",
  projectDesc = "",
  aiResults = {},
  onAIFill,
  onAIFillComplete,
  onAISources,
}: ImaginaryWheelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(500);

  // Hovered / pinned segment
  const [hovered, setHovered] = useState<{ qid: string; sid: string } | null>(null);
  const [pinned, setPinned] = useState<{ qid: string; sid: string } | null>(null);
  // Manually unfolded quadrants
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // View mode: HTML5 canvas or original image
  const [viewMode, setViewMode] = useState<"canvas" | "image">("canvas");

  // AI state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Responsive size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => {
      const w = el.clientWidth;
      setSize(Math.max(280, Math.min(w, 560)));
    });
    obs.observe(el);
    setSize(Math.max(280, Math.min(el.clientWidth, 560)));
    return () => obs.disconnect();
  }, []);

  // Draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scale = window.devicePixelRatio || 1;
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(scale, scale);

    const cx = size / 2;
    const cy = size / 2;
    const s = size / 500; // scale factor

    const r1 = R1 * s, r2 = R2 * s, r3 = R3 * s, r4 = R4 * s, r5 = R5 * s;

    ctx.clearRect(0, 0, size, size);

    // ── Draw each quadrant ──────────────────────────────────────────────────
    for (const q of SUNDIAL_QUADRANTS) {
      const segCount = q.items.length;
      const totalAngle = q.endRad - q.startRad;
      const segAngle = totalAngle / segCount;

      for (let i = 0; i < segCount; i++) {
        const item = q.items[i];
        const sa = q.startRad + i * segAngle + SEG_GAP / 2;
        const ea = q.startRad + (i + 1) * segAngle - SEG_GAP / 2;

        const isHov = hovered?.qid === q.id && hovered.sid === item.id;
        const isPin = pinned?.qid === q.id && pinned.sid === item.id;
        const isActive = isHov || isPin;

        const colorIdx = Math.min(i + 1, q.colors.length - 2); // 1..4

        // ── Inner segment (r1→r2) ──────────────────────────────────────────
        const innerPath = annularSectorPath(cx, cy, r1, r2, sa, ea);
        ctx.fillStyle = isActive ? q.colors[colorIdx + 1] : q.colors[colorIdx];
        ctx.fill(innerPath);

        if (isActive) {
          ctx.save();
          ctx.shadowColor = "rgba(0,0,0,0.35)";
          ctx.shadowBlur = 8 * s;
          ctx.fillStyle = q.colors[colorIdx + 1];
          ctx.fill(innerPath);
          ctx.restore();
        }

        // White arc border
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1.5 * s;
        ctx.stroke(innerPath);

        // Label inside inner segment — curved arc text
        drawArcText(ctx, item.label, cx, cy, (r1 + r2) / 2, sa, ea,
          Math.round(11 * s), isActive ? "#fff" : q.textDark, 14);

        // ── Outer segment (r2→r3) ─────────────────────────────────────────
        const outerPath = annularSectorPath(cx, cy, r2, r3, sa, ea);
        const outerColorIdx = Math.min(colorIdx + 1, q.colors.length - 1);
        ctx.fillStyle = isActive ? q.colors[outerColorIdx + 0] : q.colors[outerColorIdx - 1];
        ctx.fill(outerPath);

        if (isActive) {
          ctx.save();
          ctx.shadowColor = "rgba(0,0,0,0.25)";
          ctx.shadowBlur = 6 * s;
          ctx.fillStyle = q.colors[outerColorIdx];
          ctx.fill(outerPath);
          ctx.restore();
        }

        ctx.strokeStyle = "white";
        ctx.lineWidth = 1.5 * s;
        ctx.stroke(outerPath);

        // Outer ring text (short inner desc)
        const outerR = (r2 + r3) / 2;
        const arcLen = outerR * (ea - sa);
        if (arcLen > 28 * s) {
          const short = item.innerDesc.split(" ").slice(0, 3).join(" ");
          drawArcText(ctx, short, cx, cy, outerR, sa, ea, Math.round(8 * s),
            isActive ? "#fff" : q.textLight, 20);
        }
      }

      // ── Label band (r3→r4) ───────────────────────────────────────────────
      const sa = q.startRad + SEG_GAP / 2;
      const ea = q.endRad - SEG_GAP / 2;
      const labelPath = annularSectorPath(cx, cy, r3, r4, sa, ea);
      ctx.fillStyle = q.colors[q.colors.length - 2];
      ctx.fill(labelPath);
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2 * s;
      ctx.stroke(labelPath);

      // Label text
      drawArcText(ctx, q.label, cx, cy, (r3 + r4) / 2, sa, ea,
        Math.round(15 * s), "#fff", 12);

      // ── Decorative tips (r4→r5): 3 pointed spikes ───────────────────────
      const tipCount = 3;
      const tipAngle = totalAngle / tipCount;
      for (let t = 0; t < tipCount; t++) {
        const tMid = q.startRad + (t + 0.5) * tipAngle;
        const tHalf = tipAngle * 0.35;
        const tipPath = new Path2D();
        const bl = polarToCart(cx, cy, r4, tMid - tHalf);
        const br = polarToCart(cx, cy, r4, tMid + tHalf);
        const tip = polarToCart(cx, cy, r5, tMid);
        tipPath.moveTo(bl.x, bl.y);
        tipPath.arc(cx, cy, r4, tMid - tHalf, tMid + tHalf);
        tipPath.lineTo(tip.x, tip.y);
        tipPath.closePath();
        ctx.fillStyle = q.colors[q.colors.length - 1];
        ctx.fill(tipPath);
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1.5 * s;
        ctx.stroke(tipPath);
      }
    }

    // ── Center circle ──────────────────────────────────────────────────────
    ctx.beginPath();
    ctx.arc(cx, cy, r1, 0, 2 * Math.PI);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1.5 * s;
    ctx.stroke();

    // Center label
    ctx.font = `bold ${Math.round(8.5 * s)}px system-ui,sans-serif`;
    ctx.fillStyle = "#6b7280";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Imagination", cx, cy - 6 * s);
    ctx.fillText("Sundial", cx, cy + 6 * s);
  }, [size, hovered, pinned]);

  useEffect(() => { draw(); }, [draw]);

  // ── Hit test ──────────────────────────────────────────────────────────────

  const hitTest = useCallback(
    (canvasX: number, canvasY: number): { qid: string; sid: string } | null => {
      const s = size / 500;
      const r1 = R1 * s, r3 = R3 * s, r4 = R4 * s;
      const cx = size / 2, cy = size / 2;
      const dx = canvasX - cx;
      const dy = canvasY - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      if (r < r1 || r > r4) return null;

      let theta = Math.atan2(dy, dx);
      if (theta < 0) theta += 2 * Math.PI;

      for (const q of SUNDIAL_QUADRANTS) {
        let qs = q.startRad;
        let qe = q.endRad;
        // normalise PLACE (3π/2 → 2π) — both already in 0..2π range
        // normalise SPACE (π → 3π/2) — also fine
        // No wrap needed as all ranges are within 0..2π
        if (r > r3 && r <= r4) {
          // Label band → entire quadrant highlight
          if (theta >= qs && theta <= qe) {
            return { qid: q.id, sid: q.items[0].id };
          }
          continue;
        }
        if (theta < qs || theta > qe) continue;
        const segAngle = (qe - qs) / q.items.length;
        const idx = Math.floor((theta - qs) / segAngle);
        const clampedIdx = Math.max(0, Math.min(idx, q.items.length - 1));
        return { qid: q.id, sid: q.items[clampedIdx].id };
      }
      return null;
    },
    [size]
  );

  const getCanvasPos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left),
      y: (e.clientY - rect.top),
    };
  };

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const { x, y } = getCanvasPos(e);
      setHovered(hitTest(x, y));
    },
    [hitTest]
  );

  const handleMouseLeave = () => setHovered(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const { x, y } = getCanvasPos(e);
      const hit = hitTest(x, y);
      if (!hit) { setPinned(null); return; }
      if (pinned?.qid === hit.qid && pinned.sid === hit.sid) {
        setPinned(null);
      } else {
        setPinned(hit);
      }
    },
    [hitTest, pinned]
  );

  // ── Touch support ─────────────────────────────────────────────────────────

  const getTouchCanvasPos = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      const { x, y } = getTouchCanvasPos(e);
      const hit = hitTest(x, y);
      if (hit) setHovered(hit);
    },
    [hitTest] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      const { x, y } = getTouchCanvasPos(e);
      const hit = hitTest(x, y);
      setHovered(null);
      if (!hit) { setPinned(null); return; }
      if (pinned?.qid === hit.qid && pinned.sid === hit.sid) {
        setPinned(null);
      } else {
        setPinned(hit);
      }
    },
    [hitTest, pinned] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── AI auto-fill (SSE streaming, one quadrant at a time) ─────────────────

  const [aiProgress, setAiProgress] = useState("");

  const handleAIFill = async () => {
    if (!projectTitle && !projectDesc) return;
    setAiLoading(true);
    setAiError("");
    setAiProgress("");
    const merged: SundialAIResults = { ...aiResults };

    try {
      const res = await fetch("/api/experiment/sundial/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: projectTitle, description: projectDesc }),
        signal: AbortSignal.timeout(300_000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` })) as { detail?: string };
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE lines
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (!json) continue;
          try {
            const evt = JSON.parse(json) as {
              type: string;
              id?: string;
              label?: string;
              results?: Record<string, string[]>;
              sources?: { doc_title: string; doc_id: string; reason?: string }[];
            };
            if (evt.type === "quadrant" && evt.id && evt.results) {
              Object.assign(merged, evt.results);
              setAiProgress(`Behandler ${evt.label ?? evt.id}…`);
              if (onAIFill) onAIFill({ ...merged });
            } else if (evt.type === "done") {
              setAiProgress("");
              if (onAISources && evt.sources) onAISources(evt.sources);
              if (onAIFillComplete) onAIFillComplete({ ...merged });
            }
          } catch { /* malformed JSON line, skip */ }
        }
      }
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "AI fejl");
    } finally {
      setAiLoading(false);
      setAiProgress("");
    }
  };

  // ── Detail panel helpers ──────────────────────────────────────────────────

  const activeHit = pinned || hovered;
  const activeQ = activeHit ? SUNDIAL_QUADRANTS.find((q) => q.id === activeHit.qid) : null;
  const activeItem = activeQ?.items.find((i) => i.id === activeHit?.sid) ?? null;

  const toggleExpanded = (qid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid); else next.add(qid);
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">

      {/* Top bar: view toggle + AI button */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* View toggle */}
        <div className="flex rounded-xl overflow-hidden border border-gray-200 shrink-0">
          <button
            onClick={() => setViewMode("canvas")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === "canvas"
                ? "bg-purple-700 text-white"
                : "bg-white text-gray-500 hover:bg-gray-50"
            }`}
          >
            🎯 HTML5
          </button>
          <button
            onClick={() => setViewMode("image")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 ${
              viewMode === "image"
                ? "bg-purple-700 text-white"
                : "bg-white text-gray-500 hover:bg-gray-50"
            }`}
          >
            🖼️ Original
          </button>
        </div>

        {viewMode === "canvas" && (
          <button
            onClick={handleAIFill}
            disabled={aiLoading || (!projectTitle && !projectDesc)}
            className="flex items-center gap-2 px-3 sm:px-4 py-1.5 text-sm text-white rounded-xl disabled:opacity-50"
            style={{ backgroundColor: "#7b2d8b" }}
            title="Udfyld Imagination Sundial med AI baseret på projektets titel og beskrivelse"
          >
            {aiLoading
              ? <><span className="animate-spin inline-block">⚙️</span> <span className="hidden sm:inline">{aiProgress || "AI analyserer…"}</span><span className="sm:hidden">AI…</span></>
              : <>✨ <span className="sm:hidden">AI</span><span className="hidden sm:inline">AI: Udfyld Imagination Sundial</span></>}
          </button>
        )}

        {viewMode === "canvas" && (
          <span className="text-xs text-gray-400">
            <span className="hidden sm:inline">Hover for detaljer · </span>Tryk for at fastgøre
          </span>
        )}
        {aiError && <span className="text-xs text-red-600">{aiError}</span>}
      </div>

      {/* Original image view */}
      {viewMode === "image" && (
        <div className="flex justify-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/Imagination-Sundial-300dpi-scaled.webp"
            alt="Imagination Sundial © 2020 Rob Shorter, Co-Created with Rob Hopkins — CC BY-SA 4.0"
            className="rounded-xl shadow-md"
            style={{ maxWidth: "100%", width: 540 }}
          />
        </div>
      )}

      {/* Canvas wheel */}
      <div ref={containerRef} className={viewMode === "canvas" ? "flex justify-center" : "hidden"}>
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          style={{ width: size, height: size, cursor: "pointer", borderRadius: "50%", touchAction: "manipulation" }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        />
      </div>

      {/* ── Hover/pin detail panel ─── */}
      {viewMode === "canvas" && activeItem && activeQ && (
        <div
          className="rounded-xl border p-3 sm:p-4 transition-all"
          style={{ borderColor: activeQ.colors[3], backgroundColor: activeQ.colors[0] }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <span
                className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                style={{ backgroundColor: activeQ.colors[4], color: "#fff" }}
              >
                {activeQ.label}
              </span>
              <h4 className="font-bold text-gray-900 mt-1">{activeItem.label}</h4>
            </div>
            {pinned && (
              <button
                onClick={() => setPinned(null)}
                className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
              >
                ✕ Luk
              </button>
            )}
          </div>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">
            {activeItem.outerDesc}
          </p>
          {aiResults[activeItem.id]?.some(Boolean) && (
            <div
              className="mt-2 p-3 rounded-lg border-l-4 text-sm text-gray-800 leading-relaxed"
              style={{ borderColor: activeQ.colors[4], backgroundColor: "rgba(255,255,255,0.6)" }}
            >
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: activeQ.colors[4] }}>
                AI — {activeItem.label}
              </span>
              <ol className="mt-1 flex flex-col gap-1 list-decimal list-inside">
                {aiResults[activeItem.id].filter(Boolean).map((txt, i) => (
                  <li key={i} className="leading-snug">{txt}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* ── Manual quadrant unfold list ─── */}
      <div className="flex flex-col gap-2">
        {SUNDIAL_QUADRANTS.map((q) => {
          const isOpen = expanded.has(q.id);
          return (
            <div key={q.id} className="rounded-xl border overflow-hidden"
              style={{ borderColor: q.colors[3] }}>
              {/* Header */}
              <button
                className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 text-left transition-colors hover:opacity-90"
                style={{ backgroundColor: q.colors[4], color: "#fff" }}
                onClick={() => toggleExpanded(q.id)}
              >
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-sm">{q.label}</span>
                  <span className="text-xs ml-2 opacity-80 hidden sm:inline">— {q.subtitle}</span>
                </div>
                <span className="text-sm font-bold ml-2 shrink-0">{isOpen ? "▲" : "▼"}</span>
              </button>

              {/* Expanded item list */}
              {isOpen && (
                <div className="divide-y" style={{ ["--tw-divide-color" as string]: q.colors[1] }}>
                  {q.items.map((item) => {
                    const isActive = activeHit?.qid === q.id && activeHit.sid === item.id;
                    return (
                      <div
                        key={item.id}
                        className="px-4 py-2.5 cursor-pointer transition-colors"
                        style={{ backgroundColor: isActive ? q.colors[1] : q.colors[0] }}
                        onMouseEnter={() => setHovered({ qid: q.id, sid: item.id })}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => {
                          setPinned(
                            pinned?.qid === q.id && pinned.sid === item.id
                              ? null
                              : { qid: q.id, sid: item.id }
                          );
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm" style={{ color: q.textDark }}>
                              {item.label}
                            </p>
                            <p className="text-xs text-gray-600 leading-relaxed mt-0.5">
                              {item.outerDesc}
                            </p>
                            {aiResults[item.id]?.some(Boolean) && (
                              <ol
                                className="text-xs mt-1.5 px-2 py-1 rounded leading-relaxed list-decimal list-inside flex flex-col gap-0.5"
                                style={{ backgroundColor: q.colors[1], color: q.textDark }}
                              >
                                {aiResults[item.id].filter(Boolean).map((txt, i) => (
                                  <li key={i}>{txt}</li>
                                ))}
                              </ol>
                            )}
                          </div>
                          {isActive && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded-full shrink-0"
                              style={{ backgroundColor: q.colors[4], color: "#fff" }}
                            >
                              aktiv
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Attribution */}
      <p className="text-[10px] text-gray-400 text-left sm:text-right leading-relaxed">
        Imagination Sundial © 2020 Rob Shorter, Co-Created with Rob Hopkins — CC BY-SA 4.0 ·{" "}
        <a
          href="https://doughnuteconomics.org/members/4"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          Rob Shorter
        </a>
        {" · "}
        <a
          href="https://www.thedeepdivepod.com/40"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          The Deep Dive Pod
        </a>
        {" · "}
        <a
          href="https://www.robhopkins.net/2020/06/30/introducing-the-imagination-sundial/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          robhopkins.net
        </a>
        {" · "}
        <a
          href="https://www.youtube.com/watch?v=433jaIvPLSU"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          Ministry of Imagination × BLOXHUB
        </a>
        {" · "}
        <a
          href="https://www.youtube.com/watch?v=8D3eKHr3znI"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          Konst + Ekonomi, Rob Shorter: Doughnut Economics
        </a>
      </p>
    </div>
  );
}
